import { breakpoints } from '@galapagos/design-system/breakpoints';
import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';
import { duration, easing } from '@galapagos/design-system/animations';
import { shadows } from '@galapagos/design-system/web';

type TypographyToken = keyof typeof typography;

export const REWARDS_CAROUSEL_STYLE_ID = 'signup-rewards-carousel-style';

/** The gap between the card and the screen's edges; the reserved room below the page counts it too. */
export const REWARDS_CAROUSEL_GUTTER = spacing.m;

/**
 * Custom property, set on the document root while the card shows, carrying the
 * phone dock's height. The step's sticky button bar sits that far up, on top
 * of the dock.
 */
export const REWARDS_DOCK_HEIGHT_VAR = '--signup-rewards-dock-height';

/**
 * Custom property, set beside the dock height, carrying the dock plus the
 * pinned button bar above it: how far from the bottom a focused field must
 * stay to be seen.
 */
export const REWARDS_SCROLL_CLEARANCE_VAR = '--signup-rewards-scroll-clearance';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

function shadow(token: keyof typeof shadows): string {
  const s = shadows[token];
  return `${s.offset.width}px ${s.offset.height}px ${s.radius}px ${s.spread}px ${s.color}`;
}

/**
 * The rewards card (production capture "Address – rewards pop-up"). Phones get
 * it in a full-width dock along the bottom edge, below the step's sticky
 * button bar, which rides on top of the dock (the dock-height property), so
 * the card never covers Continue or a field; scrolling a focused field into
 * view stops above the bar and the dock. The dock is capped at half the
 * screen so the bar and some of the step always stay in view. Wider screens
 * get the card in the bottom-right corner, no wider than the signup column.
 * Production's 390px card, 136px product shot and 168px top panel have no
 * tokens, so the column width, `illustration.md` and `illustration.panelHeight`
 * stand in. Items share one grid cell so the card keeps the height of its
 * tallest item while they cross-fade; reduced motion swaps them without the
 * fade. On a short or zoomed screen the card's contents scroll while the close
 * button, outside the scrolling card, stays in view. A spacer after the page
 * reserves the card's height, so anything it covers, an inline Continue
 * included, can be scrolled clear of it. Production stacks the card over the
 * page (z-index 1); no token covers stacking order.
 */
export function rewardsCarouselCss(): string {
  const gutter = REWARDS_CAROUSEL_GUTTER;
  return `
.signup-rewards-carousel { position: fixed; z-index: 1; left: 0; right: 0; bottom: 0; box-sizing: border-box; max-height: 50dvh; padding: ${gutter}px ${gutter}px calc(${gutter}px + env(safe-area-inset-bottom)); background: ${colors.background.container}; display: flex; flex-direction: column; }
/* A closed card leaves only its announcement behind: no dock box. */
.signup-rewards-carousel[data-dismissed='true'] { padding: 0; background: none; }
html { scroll-padding-bottom: var(${REWARDS_SCROLL_CLEARANCE_VAR}, 0px); }
.signup-rewards-carousel > div { display: flex; flex-direction: column; min-height: 0; max-height: 100%; width: 100%; max-width: ${sizing.column.max}px; margin: 0 auto; }
.signup-rewards-carousel__card { background: ${colors.background.container}; border: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; border-radius: ${radius.lg}px; box-shadow: ${shadow('shadow400')}; overflow-x: hidden; overflow-y: auto; min-height: 0; }
.signup-rewards-carousel__items { display: grid; }
.signup-rewards-carousel__item { grid-area: 1 / 1; opacity: 0; transition: opacity ${duration.crossfade}ms cubic-bezier(${easing.standard.join(', ')}); pointer-events: none; }
.signup-rewards-carousel__item[data-active='true'] { opacity: 1; pointer-events: auto; }
.signup-rewards-carousel__top { display: flex; align-items: center; gap: ${spacing.s}px; height: ${sizing.illustration.panelHeight}px; padding: ${spacing.m}px ${spacing.sm}px; box-sizing: border-box; background-size: cover; background-position: center; }
.signup-rewards-carousel__image { flex: 1 1 0; min-width: 0; max-width: ${sizing.illustration.md}px; max-height: 100%; object-fit: contain; }
.signup-rewards-carousel__top-text { display: flex; flex-direction: column; align-items: flex-start; gap: ${spacing.s}px; padding-right: ${sizing.touchTarget.minimum}px; }
.signup-rewards-carousel__name { margin: 0; color: ${colors.text.primary}; ${font('b1Bold')} }
.signup-rewards-carousel__pill { margin: 0; padding: ${spacing.xs}px ${spacing.sm}px; border-radius: ${radius.rounded}px; background: ${colors.misc.variationQ}; color: ${colors.text.primary}; ${font('c1Medium')} }
.signup-rewards-carousel__pill strong { font-weight: ${typography.b3Bold.fontWeight}; }
.signup-rewards-carousel__bottom { display: flex; flex-direction: column; gap: ${spacing.s}px; padding: ${spacing.m}px ${spacing.lg}px ${spacing.lg}px; }
.signup-rewards-carousel__heading { margin: 0; color: ${colors.text.primary}; ${font('b2Bold')} }
.signup-rewards-carousel__body { margin: 0; color: ${colors.text.secondary}; ${font('b3Regular')} }
@media (prefers-reduced-motion: reduce) {
  .signup-rewards-carousel__item { transition: none; }
}
@media (min-width: ${breakpoints.column}px) {
  html { scroll-padding-bottom: 0; }
  .signup-rewards-carousel { left: auto; right: ${spacing.lg}px; bottom: ${spacing.lg}px; width: ${sizing.column.max}px; max-height: calc(100dvh - ${2 * spacing.lg}px); padding: 0; background: none; }
}
`;
}

/** Injects the card stylesheet once per document. No-op during prerender. */
export function ensureRewardsCarouselStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(REWARDS_CAROUSEL_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = REWARDS_CAROUSEL_STYLE_ID;
  style.textContent = rewardsCarouselCss();
  document.head.appendChild(style);
}
